import { useQuery, useMutation, QueryClient } from '@tanstack/react-query';
import apiClient from '../lib/apiClient';
import { z } from "zod";
import { isAxiosError } from 'axios';
import { ChannelSchema, ChannelsSchema } from './channelService';

// --- Query Client (Instantiate if not already global) ---
// export const queryClient = new QueryClient(); // Or use an existing one

// --- Schemas ---

export const PlanFeatureSchema = z.object({
  name: z.string(),
  icon: z.string().nullable().optional(),
  included: z.boolean(),
});

export const FeatureSchema = z.object({
  name: z.string(),
  icon: z.string().nullable().optional(),
  id: z.number(),
});

// Schema para PlanFeature con detalles completos (para getPlanById)
export const PlanFeatureDetailSchema = z.object({
  id: z.number(),
  planId: z.number(),
  featureId: z.number(),
  included: z.boolean(),
  feature: z.object({
    id: z.number(),
    name: z.string(),
    icon: z.string(),
  }),
});

export type PlanFeatureDetail = z.infer<typeof PlanFeatureDetailSchema>;

export type Feature = z.infer<typeof FeatureSchema>;

export const PlanSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  price: z.number(),
  icon: z.string().nullable(),
  features: z.array(PlanFeatureSchema),
  popular: z.boolean(),
  limit: z.number().nullable(),
  active: z.boolean(),
  // createdAt: z.string().optional(), // si decides incluirlo
  // updatedAt: z.string().optional(),
});

// Schema específico para el plan con detalles completos (getPlanById)
export const PlanDetailSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  price: z.number(),
  icon: z.string().nullable(),
  planFeatures: z.array(PlanFeatureDetailSchema),
  popular: z.boolean(),
  limit: z.number().nullable(),
  active: z.boolean(),
});

export type PlanDetail = z.infer<typeof PlanDetailSchema>;

export const BillingPeriodSchema = z.object({
  id: z.number(),
  label: z.string(),
  months: z.number(),
  discountPercent: z.number(),
  active: z.boolean(),
  // createdAt: z.string().optional(),
  // updatedAt: z.string().optional(),
});

export const SubscriptionSchema = z.object({
  id: z.string(),
  plan: PlanSchema,
  channel: ChannelSchema.nullable(), // Puede ser null si el canal fue eliminado
  autoRenewal: z.boolean(),
  endDate: z.string(),
  active: z.boolean(),
  // ... otros campos opcionales si los agregas
});

export const CreateSubscriptionPayloadSchema = z.object({
  planId: z.string(),
  channelId: z.string(),
  autoRenewal: z.boolean(),
  // ... otros campos si agregas detalles de pago
});

export const UpdateChannelPayloadSchema = z.object({
  subscriptionId: z.string(),
  newChannelId: z.string(),
});

export const UnlinkChannelPayloadSchema = z.object({
  subscriptionId: z.string(),
});

export const UpdateAutoRenewalPayloadSchema = z.object({
  subscriptionId: z.string(),
  autoRenewal: z.boolean(),
});

export const RenewSubscriptionPayloadSchema = z.object({
  subscriptionId: z.string(), // en la URL
  paymentMethodId: z.string(), // en el body
});

export const UserPaymentMethodSchema = z.object({
  id: z.string(),
  stripePaymentMethodId: z.string(),
  cardBrand: z.string(),
  last4: z.string(),
  expMonth: z.number(),
  expYear: z.number(),
  isDefault: z.boolean(),
  createdAt: z.string(), // o z.date() si parseas como Date
  updatedAt: z.string(), // idem
  userId: z.string(),
});

// --- Inferred Types ---

export type UserChannel = z.infer<typeof ChannelSchema>;
export type Plan = z.infer<typeof PlanSchema>;
export type BillingPeriod = z.infer<typeof BillingPeriodSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type CreateSubscriptionPayload = z.infer<typeof CreateSubscriptionPayloadSchema>;
export type UpdateChannelPayload = z.infer<typeof UpdateChannelPayloadSchema>;
export type UnlinkChannelPayload = z.infer<typeof UnlinkChannelPayloadSchema>;
export type UpdateAutoRenewalPayload = z.infer<typeof UpdateAutoRenewalPayloadSchema>;
export type RenewSubscriptionPayload = z.infer<typeof RenewSubscriptionPayloadSchema>;
export type UserPaymentMethod = z.infer<typeof UserPaymentMethodSchema>;


// --- API Endpoints (Constants) ---
const CHANNELS_API_URL = '/channels/mine'; // Corrected, assuming gateway prefix is handled by apiClient
const PLANS_API_URL = '/plans';
const SUBSCRIPTIONS_API_URL = '/subscriptions';
const BILLING_PERIODS_API_URL = '/billing-periods'; // New constant
const BACKOFFICE_API_URL = '/backoffice'; // New constant


// --- TanStack Query Hooks ---

export const getUserChannel = async () => {

      try {
        const data = await apiClient.get(CHANNELS_API_URL);
        
        const response = ChannelsSchema.safeParse(data)
        
        if(response.success) {
            return response.data
        }
        
    } catch (error) {
        console.log(error);
      
        if(isAxiosError(error) && error.response) {
            throw new Error("Error in getUserChannel")
        }
    }
}

interface AddPaymentMethodPayload {
  stripePaymentMethodId: string;
  cardBrand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault?: boolean;
}

export const addPaymentMethod = async (payload: AddPaymentMethodPayload) => {
  try {
    // Assuming apiClient handles the full URL and potential errors
    const response = await apiClient.post<UserPaymentMethod>('/payments/payment-methods', payload);
    return response; // Or response.data if apiClient is configured to return that directly
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
};
// export const useAddPaymentMethod = () => {
//   return useMutation<UserPaymentMethod, Error, AddPaymentMethodPayload>({
//     mutationFn: async (payload) => {
//       try {
//         const response = await apiClient.post<UserPaymentMethod>('/payments/payment-methods', payload);
//         console.log({response})
//         return response;
//       } catch (err: any) {
//         console.error('Error adding payment method:', err);
//         throw err; // Propaga el error para que lo capture onError
//       }
//     },
//     onError: (error) => {
//       console.error('useAddPaymentMethod:', error);
//     },
//     // Puedes invalidar queries si lo necesitas:
//     // onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userPaymentMethods'] }),
//   });
// };

// --- Payment Processing ---

export const PaymentItemSchema = z.object({
  id: z.string(), 
  quantity: z.number().int().positive(),
  price: z.number().positive(), 
})

export type PaymentItem = z.infer<typeof PaymentItemSchema>;

export const ProcessPaymentPayloadSchema = z.object({
  items: z.array(PaymentItemSchema),
  selectedPaymentMethodId: z.string(), 
  channelId: z.string(),
  autoRenewal: z.boolean(),
})

export type ProcessPaymentPayload = z.infer<typeof ProcessPaymentPayloadSchema>;

export const ProcessPaymentResponseSchema = z.object({
  success: z.boolean(),
  paymentIntentId: z.string(),
  message: z.string(),
})

export type ProcessPaymentResponse = z.infer<typeof ProcessPaymentResponseSchema>;

export const processSubscriptionPayment = async (payload: ProcessPaymentPayload) => {
  try {
    const data = await apiClient.post('/payments/process-payment', payload);
    const response = ProcessPaymentResponseSchema.safeParse(data)
    console.log(data, response);
    
    if(response.success) {
        return response.data
    }
  } catch (error) {
    console.error('Error processing subscription payment:', error);
    throw error;
  }
};

// --- Payment Method Management ---

export const PaymentMethodSchema = z.object({
  id: z.string(), 
  stripePaymentMethodId: z.string(),
  cardBrand: z.string(),
  last4: z.string(),
  expMonth: z.number(),
  expYear: z.number(),
  isDefault: z.boolean(),
})
export const PaymentMethodsSchema = z.array(PaymentMethodSchema);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const listPaymentMethods = async () => {
  try {
    const data = await apiClient.get('/payments/payment-methods');
    const response = PaymentMethodsSchema.safeParse(data)
    
    if(response.success) {
        return response.data
    }
  } catch (error) {
    console.error('Error listing payment methods:', error);
    throw error;
  }
};

export const deletePaymentMethod = async (paymentMethodId: string): Promise<void> => {
  try {
    await apiClient.delete(`/payments/payment-methods/${paymentMethodId}`);
  } catch (error) {
    console.error(`Error deleting payment method ${paymentMethodId}:`, error);
    throw error;
  }
};

export const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<void> => {
  try {
    await apiClient.post(`/payments/payment-methods/${paymentMethodId}/set-default`, {});
  } catch (error) {
    console.error(`Error setting default payment method ${paymentMethodId}:`, error);
    throw error;
  }
};

// Fetch Plans
export const usePlans = () => {
  return useQuery<Plan[], Error>({ // Plan[] is now using the updated interface
    queryKey: ['plans'],
    queryFn: async () => {
      return apiClient.get<Plan[]>(PLANS_API_URL);
    },
  });
};

// Fetch Billing Periods (New Hook)
export const useBillingPeriods = () => {
  return useQuery<BillingPeriod[], Error>({
    queryKey: ['billingPeriods'],
    queryFn: async () => {
      return apiClient.get<BillingPeriod[]>(BILLING_PERIODS_API_URL);
    },
  });
};

// Fetch User Subscriptions
export const useUserSubscriptions = () => {
  return useQuery<Subscription[], Error>({
    queryKey: ['userSubscriptions'],
    queryFn: async () => {
      return apiClient.get<Subscription[]>(SUBSCRIPTIONS_API_URL);
    },
  });
};

// Create Subscription
export const useCreateSubscription = (queryClient: QueryClient) => {
  return useMutation<Subscription, Error, CreateSubscriptionPayload>({
    mutationFn: async (payload) => {
      return apiClient.post<Subscription>(SUBSCRIPTIONS_API_URL, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] });
    },
  });
};

// Update Subscription Channel
export const useUpdateSubscriptionChannel = (queryClient: QueryClient) => {
  return useMutation<Subscription, Error, UpdateChannelPayload>({
    mutationFn: async ({ subscriptionId, newChannelId }) => {
      return apiClient.put<Subscription>(`${SUBSCRIPTIONS_API_URL}/${subscriptionId}/channel`, { newChannelId });
    },
  });
};

// Unlink Channel from Subscription
export const useUnlinkSubscriptionChannel = (queryClient: QueryClient) => {
  return useMutation<Subscription, Error, UnlinkChannelPayload>({
    mutationFn: async ({ subscriptionId }) => {
      return apiClient.delete<Subscription>(`${SUBSCRIPTIONS_API_URL}/${subscriptionId}/channel`);
    },
  });
};

export const updateSubscriptionAutoRenewal = async ({ subscriptionId, autoRenewal }: UpdateAutoRenewalPayload) => {
  try {
    const data = await apiClient.put(`${SUBSCRIPTIONS_API_URL}/${subscriptionId}/auto-renewal`, { autoRenewal });
    
    const response = SubscriptionSchema.safeParse(data)
    console.log(data, response);
    if(response.success) {
        return response.data
    }
    
  } catch (error) {
    console.log(error);

  }
}

// Renew Subscription
export const useRenewSubscription = (queryClient: QueryClient) => {
  return useMutation<Subscription, Error, RenewSubscriptionPayload>({
    mutationFn: async ({ subscriptionId, paymentMethodId }) => {
      const response = await apiClient.post<Subscription>(
        `/subscriptions/${subscriptionId}/renew-manually`,
        { paymentMethodId }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] });
    },
    onError: (error) => {
      console.error('Renewal error:', error);
    },
  });
};

export const fetchPlans = async (): Promise<PlanDetail[]> => {
  const data = await apiClient.get(`${BACKOFFICE_API_URL}/plans`);
  const parsed = z.array(PlanDetailSchema).safeParse(data);
  console.log({data, parsed});
  
  if (!parsed.success) {
    throw new Error('Respuesta inválida al obtener planes');
  }

  return parsed.data;
};


//backoffice
export const getPlans = async (): Promise<Plan[]> => {
  const data = await apiClient.get(PLANS_API_URL);
  const parsed = z.array(PlanSchema).safeParse(data);
  if (!parsed.success) {
    throw new Error('Respuesta inválida al obtener planes');
  }

  return parsed.data;
};

export const getPlanById = async (planId: number): Promise<PlanDetail> => {
  const data = await apiClient.get(`${BACKOFFICE_API_URL}/plans/${planId}`);
  const parsed = PlanDetailSchema.safeParse(data);

  console.log(data, parsed);
  
  if (!parsed.success) {
    console.error('Parse error:', parsed.error);
    throw new Error('Respuesta inválida al obtener plan');
  }

  return parsed.data;
};

export const putPlan = async ({planId, payload}: {planId: number, payload: CreatePlanPayload})=> {
  const data = await apiClient.put(`${BACKOFFICE_API_URL}/plans/${planId}`, payload);
  return data
};

// --- Stripe Settings ---

const StripeSettingSchema = z.object({ stripeEnabled: z.boolean() });

export const getStripeEnabled = async (): Promise<boolean> => {
  const data = await apiClient.get('/settings/stripe-enabled');
  return StripeSettingSchema.parse(data).stripeEnabled;
};

export const useStripeEnabled = () => {
  return useQuery<boolean, Error>({
    queryKey: ['stripeEnabled'],
    queryFn: getStripeEnabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const getStripeEnabledAdmin = async (): Promise<boolean> => {
  const data = await apiClient.get(`${BACKOFFICE_API_URL}/settings/stripe-enabled`);
  return StripeSettingSchema.parse(data).stripeEnabled;
};

export const updateStripeEnabledAdmin = async (enabled: boolean): Promise<boolean> => {
  const data = await apiClient.put(`${BACKOFFICE_API_URL}/settings/stripe-enabled`, { enabled });
  return StripeSettingSchema.parse(data).stripeEnabled;
};

// --- Manual Renewal Message ---

const ManualRenewalMessageSchema = z.object({
  language: z.string(),
  message: z.string(),
});

export const getManualRenewalMessages = async (): Promise<{ language: string; message: string }[]> => {
  const data = await apiClient.get('/settings/manual-renewal-messages');
  return z.array(ManualRenewalMessageSchema).parse(data);
};

export const useManualRenewalMessages = () => {
  return useQuery<{ language: string; message: string }[], Error>({
    queryKey: ['manualRenewalMessages'],
    queryFn: getManualRenewalMessages,
    staleTime: 5 * 60 * 1000,
  });
};

export const getManualRenewalMessagesAdmin = async (): Promise<{ language: string; message: string }[]> => {
  const data = await apiClient.get(`${BACKOFFICE_API_URL}/settings/manual-renewal-messages`);
  return z.array(ManualRenewalMessageSchema).parse(data);
};

export const updateManualRenewalMessage = async ({
  language,
  message,
}: {
  language: string;
  message: string;
}): Promise<{ language: string; message: string }> => {
  const data = await apiClient.put(`${BACKOFFICE_API_URL}/settings/manual-renewal-messages`, {
    language,
    message,
  });
  return ManualRenewalMessageSchema.parse(data);
};

export const createPaymentManualBackoffice = async (payload: {planId: number, channelId: string, months: number}) => {
  try {
    const createdData = await apiClient.post("/payments/process-manual-payment", {...payload, channelId: Number(payload.channelId)});
    return createdData;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
};

export const CreatePlanPayloadSchema = z.object({
  name: z.string(),
  description: z.string(),
  price: z.number(),
  icon: z.string().nullable(),
  features: z.array(z.object({
      featureId: z.number(),
      included: z.boolean()
    })
  ),
  popular: z.boolean(),
  limit: z.number().nullable(),
  active: z.boolean(),
});

export type CreatePlanPayload = z.infer<typeof CreatePlanPayloadSchema>;

export const createPlan = async (payload: CreatePlanPayload) => {
  try {

    const createdData = await apiClient.post(BACKOFFICE_API_URL + "/plans", payload);

    return createdData;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
};

export const createFeature = async (icon: string, name: string) => {
  try {
    const createdData = await apiClient.post(BACKOFFICE_API_URL + "/features", { icon, name });

    const response = FeatureSchema.safeParse(createdData);

    if(!response.success) {
      throw new Error("Error fetching features");
    }
    return response.data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
}

export const getFeatures = async () => {
  try {
    const data = await apiClient.get(BACKOFFICE_API_URL + "/features");

    const response = z.array(FeatureSchema).safeParse(data);

    if(!response.success) {
      throw new Error("Error fetching features");
    }
    return response.data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
}

export const deleteFeature = async (featureId: number) => {
  try {
    const deleted = await apiClient.delete(BACKOFFICE_API_URL + "/features" + `/${featureId}`);
    const response = z.object({message: z.string(), id: z.number()}).safeParse(deleted);

    if(!response.success) {
      throw new Error("Error fetching features");
    }
    return response.data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
}
